import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Mail, Plus, X, FileDown, Pencil, Trash2, Paperclip, RefreshCw, Send,
  Download, Upload,
} from "lucide-react";
import DepartmentFilter, { useDepartmentFilter } from "@/components/DepartmentFilter";
import FirmFilter from "@/components/FirmFilter";
import EmployeeStatusFilter from "@/components/EmployeeStatusFilter";
import { fmtDateTime } from "@/lib/format";
import EmployeePicker from "@/components/EmployeePicker";
import VehiclePicker from "@/components/VehiclePicker";
import { usePermissions } from "@/contexts/PermissionsContext";
import ModuleHint from "@/components/ModuleHint";

export default function Lenkerauskunft() {
  const { can } = usePermissions();
  const canCreate = can("operations", "create");
  const canEdit = can("operations", "edit");
  const canDelete = can("operations", "delete");

  const [items, setItems] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState("");

  // Filters
  const [deptFilter, setDeptFilter] = useDepartmentFilter("lenkerauskunft");
  const [empStatus, setEmpStatus] = useState("aktiv");
  const [vehicleFilter, setVehicleFilter] = useState("");
  const [empFilter, setEmpFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  async function load() {
    setLoading(true);
    try {
      const params = {};
      if (vehicleFilter) params.vehicle_id = vehicleFilter;
      if (empFilter) params.employee_id = empFilter;
      if (deptFilter) params.department_id = deptFilter;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const { data } = await api.get("/lenkerauskunft", { params });
      setItems(data);
    } catch { toast.error("Laden fehlgeschlagen"); }
    finally { setLoading(false); }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [vehicleFilter, empFilter, deptFilter, dateFrom, dateTo]);
  useEffect(() => {
    api.get("/vehicles").then(({ data }) => setVehicles(data));
    api.get("/employees", { params: empStatus ? { status: empStatus } : {} }).then(({ data }) => setEmployees(data));
  }, [empStatus]);

  const empName = (id) => {
    const e = employees.find(x => x.id === id);
    return e ? `${e.first_name} ${e.last_name}` : "?";
  };
  const vehLabel = (id) => vehicles.find(x => x.id === id)?.license_plate || "?";

  async function downloadPdf(l) {
    try {
      const res = await api.get(`/lenkerauskunft/${l.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `lenkerauskunft_${vehLabel(l.vehicle_id)}_${(l.datetime || "").slice(0, 10)}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("PDF-Download fehlgeschlagen"); }
  }

  async function removeItem(l) {
    if (!window.confirm(`Lenkerauskunft für ${vehLabel(l.vehicle_id)} vom ${(l.datetime || "").slice(0, 10)} wirklich löschen?`)) return;
    try {
      await api.delete(`/lenkerauskunft/${l.id}`);
      toast.success("Eintrag gelöscht");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Löschen fehlgeschlagen"); }
  }

  const filteredItems = useMemo(() => {
    if (!search.trim()) return items;
    const q = search.toLowerCase();
    return items.filter(l =>
      (vehLabel(l.vehicle_id) || "").toLowerCase().includes(q) ||
      (empName(l.employee_id) || "").toLowerCase().includes(q) ||
      (l.subject || "").toLowerCase().includes(q)
    );
  // eslint-disable-next-line
  }, [items, search, vehicles, employees]);

  return (
    <div className="p-6 md:p-8" data-testid="lenkerauskunft-page">
      <ModuleHint moduleKey="lenkerauskunft" />
      <div className="flex items-end justify-between mb-5 flex-wrap gap-3">
        <div>
          <h2 className="font-display text-xl font-semibold text-zinc-100 inline-flex items-center gap-2">
            <Mail className="w-5 h-5 text-zinc-500"/> Lenkerauskünfte
          </h2>
          <p className="text-xs text-zinc-500 mt-0.5">
            {items.length} Einträge · Anfragen von Behörden zur Feststellung des Fahrzeug­lenkers
          </p>
        </div>
        {canCreate && (
          <button onClick={() => { setEditing(null); setShowForm(true); }}
            data-testid="la-new-btn"
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded">
            <Plus className="w-4 h-4"/> Neu hinzufügen
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="bg-zinc-900 border border-zinc-800 rounded p-3 mb-4 flex flex-wrap gap-2 items-center">
        <FirmFilter compact />
        <DepartmentFilter value={deptFilter} onChange={setDeptFilter} module="lenkerauskunft" testid="la-dept-filter"/>
        <EmployeeStatusFilter value={empStatus} onChange={setEmpStatus} testid="la-empstatus-filter" size="sm"/>
        <select value={vehicleFilter} onChange={(e) => setVehicleFilter(e.target.value)}
          data-testid="la-vehicle-filter"
          className="px-3 py-1.5 bg-zinc-950 border border-zinc-800 rounded text-xs text-zinc-100 max-w-[180px]">
          <option value="">Alle Fahrzeuge</option>
          {vehicles.map(v => <option key={v.id} value={v.id}>{v.license_plate}</option>)}
        </select>
        <select value={empFilter} onChange={(e) => setEmpFilter(e.target.value)}
          data-testid="la-emp-filter"
          className="px-3 py-1.5 bg-zinc-950 border border-zinc-800 rounded text-xs text-zinc-100 max-w-[200px]">
          <option value="">Alle Fahrer</option>
          {employees.map(e => <option key={e.id} value={e.id}>{e.first_name} {e.last_name}</option>)}
        </select>
        <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="la-date-from"
          className="px-3 py-1.5 bg-zinc-950 border border-zinc-800 rounded text-xs text-zinc-100"/>
        <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="la-date-to"
          className="px-3 py-1.5 bg-zinc-950 border border-zinc-800 rounded text-xs text-zinc-100"/>
        <input placeholder="Suchen…" value={search} onChange={(e) => setSearch(e.target.value)}
          data-testid="la-search"
          className="ml-auto px-3 py-1.5 bg-zinc-950 border border-zinc-800 rounded text-xs text-zinc-100 w-48"/>
      </div>

      {/* Table */}
      <div className="bg-zinc-900 border border-zinc-800 rounded overflow-x-auto">
        {loading ? (
          <div className="p-8 text-center text-zinc-500 text-sm">Laden…</div>
        ) : filteredItems.length === 0 ? (
          <div className="p-10 text-center text-zinc-500 text-sm">
            <Mail className="w-8 h-8 mx-auto mb-2 text-zinc-700"/>
            Noch keine Lenkerauskünfte erfasst.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead><tr className="text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
              <th className="px-4 py-3 text-left">Fahrzeug</th>
              <th className="px-4 py-3 text-left">Fahrer</th>
              <th className="px-4 py-3 text-left">Betreff</th>
              <th className="px-4 py-3 text-left">Vorfall</th>
              <th className="px-4 py-3 text-center">Anh.</th>
              <th className="px-4 py-3 text-center">Status</th>
              <th className="px-4 py-3 text-right">Aktionen</th>
            </tr></thead>
            <tbody>{filteredItems.map(l => {
              const atts = (l.attachments || []).length;
              return (
                <tr key={l.id} data-testid={`la-row-${l.id}`} className="border-b border-zinc-800 hover:bg-zinc-800/40">
                  <td className="px-4 py-2.5 text-zinc-200 font-medium font-mono">{vehLabel(l.vehicle_id)}</td>
                  <td className="px-4 py-2.5 text-zinc-300">{empName(l.employee_id)}</td>
                  <td className="px-4 py-2.5 text-zinc-300 max-w-[320px] truncate" title={l.subject}>{l.subject}</td>
                  <td className="px-4 py-2.5 text-zinc-400 font-mono text-xs">{(l.datetime || "").replace("T", " ").slice(0, 16)}</td>
                  <td className="px-4 py-2.5 text-center">
                    {atts > 0 ? <span className="inline-flex items-center gap-0.5 text-blue-400 text-[11px]"><Paperclip className="w-3 h-3"/> {atts}</span> : <span className="text-zinc-600">—</span>}
                  </td>
                  <td className="px-4 py-2.5 text-center">
                    <span className={`inline-flex text-[10px] px-2 py-0.5 rounded font-semibold uppercase ${
                      l.status === "gesendet" ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
                      : l.status === "fehlgeschlagen" ? "bg-red-500/15 text-red-300 border border-red-500/30"
                      : "bg-zinc-700/50 text-zinc-300 border border-zinc-600/40"
                    }`}>
                      {l.status || "entwurf"}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <div className="inline-flex items-center gap-2">
                      <button onClick={() => downloadPdf(l)} data-testid={`la-pdf-${l.id}`}
                        title="PDF herunterladen"
                        className="inline-flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300">
                        <FileDown className="w-3.5 h-3.5"/> PDF
                      </button>
                      {canEdit && (
                        <button onClick={() => { setEditing(l); setShowForm(true); }} data-testid={`la-edit-${l.id}`}
                          title="Bearbeiten"
                          className="inline-flex items-center text-zinc-400 hover:text-zinc-100">
                          <Pencil className="w-3.5 h-3.5"/>
                        </button>
                      )}
                      {canDelete && (
                        <button onClick={() => removeItem(l)} data-testid={`la-delete-${l.id}`}
                          title="Löschen"
                          className="inline-flex items-center text-zinc-500 hover:text-red-400">
                          <Trash2 className="w-3.5 h-3.5"/>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}</tbody>
          </table>
        )}
      </div>

      {showForm && (
        <LenkerauskunftForm
          vehicles={vehicles}
          employees={employees}
          editing={editing}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={() => { setShowForm(false); setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

// ============================================================
// LenkerauskunftForm — compose modal with template auto-fill
// Default CC is loaded from tenant settings (reseller-ready, was hardcoded before Phase 4A)
// ============================================================

function LenkerauskunftForm({ vehicles, employees, editing, onClose, onSaved }) {
  const isEdit = !!editing;
  const nowLocal = new Date().toISOString().slice(0, 16);
  const [defaultCc, setDefaultCc] = useState([]);
  const [form, setForm] = useState(() => editing ? {
    vehicle_id: editing.vehicle_id || "",
    employee_id: editing.employee_id || "",
    datetime: editing.datetime || nowLocal,
    subject: editing.subject || "",
    body: editing.body || "",
    to_email: editing.to_email || "",
    cc_emails: editing.cc_emails || [],
    attachments: editing.attachments || [],
    status: editing.status || "entwurf",
  } : {
    vehicle_id: "",
    employee_id: "",
    datetime: nowLocal,
    subject: "",
    body: "",
    to_email: "",
    cc_emails: [],
    attachments: [],
    status: "entwurf",
  });
  const [newCc, setNewCc] = useState("");
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [loadingTpl, setLoadingTpl] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Load tenant-configured default CC once on mount
  useEffect(() => {
    api.get("/settings/lenkerauskunft").then(({ data }) => {
      setDefaultCc(data.default_cc || []);
      if (!isEdit && (data.default_cc || []).length > 0) {
        setForm(f => ({ ...f, cc_emails: f.cc_emails.length > 0 ? f.cc_emails : data.default_cc }));
      }
    }).catch(() => setDefaultCc([]));
  // eslint-disable-next-line
  }, []);

  // User-CCs = entries the user added that are NOT configured defaults
  const userCc = useMemo(() => {
    const defSet = new Set((defaultCc || []).map(x => x.toLowerCase()));
    return (form.cc_emails || []).filter(e => !defSet.has(e.toLowerCase()));
  }, [form.cc_emails, defaultCc]);

  async function loadTemplate(force = false) {
    if (!form.vehicle_id || !form.employee_id) return;
    if (!force && (form.subject || form.body)) return;
    setLoadingTpl(true);
    try {
      const { data } = await api.get("/lenkerauskunft/template", {
        params: {
          vehicle_id: form.vehicle_id,
          employee_id: form.employee_id,
          datetime: form.datetime || "",
        },
      });
      setForm(f => ({ ...f, subject: data.subject, body: data.body }));
    } catch { toast.error("Vorlage konnte nicht geladen werden"); }
    finally { setLoadingTpl(false); }
  }

  // Auto-load template first time both vehicle + driver are chosen (create mode only)
  useEffect(() => {
    if (!isEdit) loadTemplate(false);
  // eslint-disable-next-line
  }, [form.vehicle_id, form.employee_id]);

  function addCc() {
    const v = (newCc || "").trim();
    if (!v || !v.includes("@")) { toast.error("Ungültige E-Mail"); return; }
    if ((defaultCc || []).map(x => x.toLowerCase()).includes(v.toLowerCase())) {
      toast.info("Diese Adresse ist bereits als Standard-Kopie eingestellt");
      return;
    }
    if (userCc.map(x => x.toLowerCase()).includes(v.toLowerCase())) {
      toast.info("E-Mail bereits in Liste");
      return;
    }
    setForm(f => ({ ...f, cc_emails: [...f.cc_emails, v] }));
    setNewCc("");
  }
  function removeCc(email) {
    setForm(f => ({ ...f, cc_emails: f.cc_emails.filter(e => e !== email) }));
  }

  async function uploadFiles(e) {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    // requires a saved entry for attachment endpoint
    if (!editing?.id && !isEdit) {
      toast.error("Bitte zuerst speichern, dann Anhänge hinzufügen");
      e.target.value = "";
      return;
    }
    const lid = editing?.id;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post(`/lenkerauskunft/${lid}/attachments`, fd,
          { headers: { "Content-Type": "multipart/form-data" } });
        setForm(ff => ({ ...ff, attachments: [...(ff.attachments || []), data] }));
      }
      toast.success(`${files.length} Datei(en) hochgeladen`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload fehlgeschlagen");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  }

  async function removeAttachment(aid) {
    if (!editing?.id) return;
    try {
      await api.delete(`/lenkerauskunft/${editing.id}/attachments/${aid}`);
      setForm(f => ({ ...f, attachments: f.attachments.filter(a => a.id !== aid) }));
      toast.success("Anhang entfernt");
    } catch { toast.error("Anhang konnte nicht entfernt werden"); }
  }

  async function downloadAttachment(a) {
    try {
      const res = await api.get(`/lenkerauskunft/${editing.id}/attachments/${a.id}`,
        { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const x = document.createElement("a");
      x.href = url; x.download = a.name;
      x.click(); window.URL.revokeObjectURL(url);
    } catch { toast.error("Download fehlgeschlagen"); }
  }

  async function save() {
    if (!form.vehicle_id) { toast.error("Fahrzeug pflicht"); return; }
    if (!form.employee_id) { toast.error("Fahrer pflicht"); return; }
    if (!form.subject?.trim()) { toast.error("Betreff pflicht"); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/lenkerauskunft/${editing.id}`, form);
        toast.success("Lenkerauskunft aktualisiert");
      } else {
        await api.post("/lenkerauskunft", form);
        toast.success("Lenkerauskunft gespeichert");
      }
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen"); }
    finally { setSaving(false); }
  }

  async function sendNow() {
    if (!isEdit || !editing?.id) {
      toast.error("Bitte zuerst speichern, bevor du versendest");
      return;
    }
    if (!form.to_email?.trim() || !form.to_email.includes("@")) {
      toast.error("Empfänger (An) fehlt oder ungültig");
      return;
    }
    if (!form.subject?.trim()) { toast.error("Betreff pflicht"); return; }
    const ok = window.confirm(
      `Diese Lenkerauskunft jetzt verschicken?\n\nAn: ${form.to_email}\nKopie: ${(form.cc_emails || []).join(", ") || "—"}\n\nPDF + ${form.attachments.length} Anhang/Anhänge werden mitgesendet.`
    );
    if (!ok) return;
    setSending(true);
    try {
      // Persist current edits first (so latest subject/body/to are used on server)
      await api.put(`/lenkerauskunft/${editing.id}`, form);
      const { data } = await api.post(`/lenkerauskunft/${editing.id}/send`);
      toast.success(`Versendet an ${data.to}`);
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Versand fehlgeschlagen");
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50" data-testid="la-form-modal">
      <div className="bg-zinc-900 border border-zinc-800 rounded-md w-full max-w-4xl max-h-[94vh] overflow-y-auto">
        <div className="p-5 border-b border-zinc-800 flex items-center justify-between sticky top-0 bg-zinc-900 z-10">
          <h3 className="font-display font-semibold text-zinc-50 inline-flex items-center gap-2">
            <Mail className="w-4 h-4 text-blue-400"/>
            {isEdit ? "Lenkerauskunft bearbeiten" : "Neue Lenkerauskunft"}
          </h3>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-100" data-testid="la-form-close">
            <X className="w-5 h-5"/>
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Core data */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-[10px] uppercase text-zinc-500">Fahrzeug *</label>
              <div className="mt-1">
                <VehiclePicker value={form.vehicle_id} testid="la-veh-picker"
                  onChange={(v) => setForm({ ...form, vehicle_id: v })} />
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase text-zinc-500">Datum / Uhrzeit *</label>
              <input type="datetime-local" value={form.datetime}
                onChange={(e) => setForm({ ...form, datetime: e.target.value })}
                data-testid="la-datetime"
                className="w-full mt-1 px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100"/>
            </div>
            <div>
              <label className="text-[10px] uppercase text-zinc-500">Fahrer *</label>
              <div className="mt-1">
                <EmployeePicker value={form.employee_id} testid="la-emp-picker"
                  onChange={(v) => setForm({ ...form, employee_id: v })} />
              </div>
            </div>
          </div>

          {/* Primary recipient */}
          <div>
            <label className="text-[10px] uppercase text-zinc-500">An (Behörde) *</label>
            <input type="email" value={form.to_email || ""}
              onChange={(e) => setForm({ ...form, to_email: e.target.value })}
              placeholder="z.B. strafen.bhmi@noel.gv.at"
              data-testid="la-to-email"
              className={`w-full mt-1 px-3 py-2 bg-zinc-950 border rounded text-sm text-zinc-100 ${!form.to_email?.trim() ? "border-amber-500/60 ring-1 ring-amber-500/30" : "border-zinc-800"}`}/>
            <div className="text-[10px] text-zinc-500 mt-1">
              Primärer Empfänger — z.B. die anfragende Bezirkshauptmannschaft oder Polizei.
            </div>
          </div>

          {/* CC */}
          <div>
            <label className="text-[10px] uppercase text-zinc-500">Kopie senden an</label>
            <div className="mt-1.5 flex flex-wrap gap-1.5 items-center">
              {(defaultCc || []).map((e) => (
                <span key={e} className="inline-flex items-center gap-1 px-2 py-1 text-[11px] bg-blue-500/10 text-blue-300 border border-blue-500/30 rounded" title="Standard-Empfänger aus Einstellungen → Lenkerauskunft">
                  {e} <span className="text-[9px] uppercase tracking-wide text-blue-400/70">std</span>
                </span>
              ))}
              {userCc.map((e) => (
                <span key={e} className="inline-flex items-center gap-1 px-2 py-1 text-[11px] bg-zinc-800 text-zinc-200 border border-zinc-700 rounded">
                  {e}
                  <button onClick={() => removeCc(e)} data-testid={`la-cc-rm-${e}`} className="ml-1 text-zinc-500 hover:text-red-400">
                    <X className="w-3 h-3"/>
                  </button>
                </span>
              ))}
              <div className="inline-flex items-center gap-1">
                <input type="email" placeholder="weitere@email.at" value={newCc}
                  onChange={(e) => setNewCc(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addCc())}
                  data-testid="la-cc-input"
                  className="px-2 py-1 bg-zinc-950 border border-zinc-800 rounded text-xs text-zinc-100 w-44"/>
                <button onClick={addCc} data-testid="la-cc-add"
                  className="px-2 py-1 text-xs bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 rounded">+</button>
              </div>
            </div>
            {(defaultCc || []).length === 0 && (
              <div className="text-[10px] text-amber-400/80 mt-1">
                Keine Standard-Empfänger konfiguriert. Unter Einstellungen → Lenkerauskunft eintragen.
              </div>
            )}
          </div>

          {/* Subject */}
          <div>
            <div className="flex items-center justify-between">
              <label className="text-[10px] uppercase text-zinc-500">Betreff & Geschäftszahlnummer *</label>
              <button onClick={() => loadTemplate(true)} disabled={loadingTpl || !form.vehicle_id || !form.employee_id}
                data-testid="la-tpl-reload"
                className="inline-flex items-center gap-1 text-[10px] text-blue-400 hover:text-blue-300 disabled:text-zinc-600">
                <RefreshCw className={`w-3 h-3 ${loadingTpl ? "animate-spin" : ""}`}/>
                Vorlage neu laden
              </button>
            </div>
            <input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })}
              placeholder="z.B. Lenkerauskunft - GZ: MIS2-V-26 8981/LE1"
              data-testid="la-subject"
              className={`w-full mt-1 px-3 py-2 bg-zinc-950 border rounded text-sm text-zinc-100 ${!form.subject?.trim() ? "border-amber-500/60 ring-1 ring-amber-500/30" : "border-zinc-800"}`}/>
          </div>

          {/* Body */}
          <div>
            <label className="text-[10px] uppercase text-zinc-500">E-Mail-Inhalt</label>
            <textarea rows={16} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })}
              data-testid="la-body"
              className="w-full mt-1 px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100 font-mono leading-relaxed"
              placeholder="Wird nach Auswahl von Fahrzeug + Fahrer automatisch befüllt."/>
            <div className="text-[10px] text-zinc-500 mt-1">
              Tipp: Der Text kann frei bearbeitet werden. Daten wie Geburtsdatum, Adresse oder Führerscheinnummer stammen aus dem Mitarbeiterprofil — fehlen Angaben, erscheinen Platzhalter wie <span className="font-mono text-amber-400">[FÜHRERSCHEIN-NR NICHT GESETZT]</span>.
            </div>
          </div>

          {/* Attachments */}
          <div>
            <label className="text-[10px] uppercase text-zinc-500 block mb-1.5">Anhänge</label>
            {!isEdit ? (
              <div className="px-3 py-2 bg-zinc-950 border border-dashed border-zinc-700 rounded text-[11px] text-zinc-500">
                Bitte zuerst speichern — Anhänge können anschließend im Bearbeiten-Modus hinzugefügt werden.
              </div>
            ) : (
              <>
                <label className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded cursor-pointer">
                  <Upload className="w-3.5 h-3.5"/>
                  {uploading ? "Lade…" : "Dateien hochladen"}
                  <input type="file" multiple className="hidden" onChange={uploadFiles} data-testid="la-upload"/>
                </label>
                {form.attachments.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {form.attachments.map((a) => (
                      <div key={a.id} data-testid={`la-att-${a.id}`}
                        className="flex items-center justify-between px-3 py-1.5 bg-zinc-950 border border-zinc-800 rounded text-xs">
                        <div className="flex items-center gap-2 min-w-0">
                          <Paperclip className="w-3 h-3 text-blue-400 shrink-0"/>
                          <span className="truncate text-zinc-200">{a.name}</span>
                          <span className="text-zinc-500 shrink-0">{((a.size || 0) / 1024).toFixed(0)} KB</span>
                        </div>
                        <div className="flex gap-2">
                          <button onClick={() => downloadAttachment(a)} className="text-blue-400 hover:text-blue-300">
                            <Download className="w-3.5 h-3.5"/>
                          </button>
                          <button onClick={() => removeAttachment(a.id)}
                            data-testid={`la-att-rm-${a.id}`}
                            className="text-zinc-500 hover:text-red-400">
                            <Trash2 className="w-3.5 h-3.5"/>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        <div className="p-5 border-t border-zinc-800 flex items-center justify-between gap-2 sticky bottom-0 bg-zinc-900">
          <div className="text-[11px] text-zinc-500">
            {isEdit && editing?.status === "gesendet" && editing?.sent_at && (
              <span className="inline-flex items-center gap-1 text-emerald-400">
                <Send className="w-3 h-3"/> Gesendet: {fmtDateTime(editing.sent_at)} → {editing.sent_to || "?"}
              </span>
            )}
            {isEdit && editing?.status === "fehlgeschlagen" && (
              <span className="inline-flex items-center gap-1 text-red-400" title={editing.last_send_error || ""}>
                ⚠ Letzter Versand fehlgeschlagen — korrigiere & versuche erneut
              </span>
            )}
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm bg-zinc-800 hover:bg-zinc-700 rounded">Abbrechen</button>
            <button onClick={save} disabled={saving || sending} data-testid="la-save-btn"
              className="px-4 py-2 text-sm bg-zinc-800 hover:bg-zinc-700 disabled:opacity-50 text-zinc-100 border border-zinc-700 rounded font-medium">
              {saving ? "Speichert…" : (isEdit ? "Speichern" : "Als Entwurf speichern")}
            </button>
            {isEdit && (
              <button onClick={sendNow} disabled={saving || sending || !form.to_email?.trim()}
                data-testid="la-send-btn"
                title={!form.to_email?.trim() ? "Empfänger (An) fehlt" : "E-Mail jetzt versenden"}
                className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 disabled:bg-zinc-800 disabled:text-zinc-500 text-white rounded font-medium inline-flex items-center gap-1.5">
                {sending ? "Sende…" : (<><Send className="w-3.5 h-3.5"/> Jetzt senden</>)}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
